import React, { useEffect, useState } from "react";
import { Linkedin, Mail, Loader2, CheckCircle2, XCircle, ExternalLink, RefreshCw, Send, Copy, AlertTriangle } from "lucide-react";
import { getLinkedInStatus, connectLinkedIn, disconnectLinkedIn, getEmailStatus, sendTestEmail } from "../../api";
import { useToast } from "../../hooks/use-toast";

const Pill = ({ ok, okText, noText }) => (
  <span className={`inline-flex items-center gap-1.5 text-[12px] px-2.5 py-1 rounded-full ${ok ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
    {ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />} {ok ? okText : noText}
  </span>
);

const Connections = () => {
  const WEBHOOK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/resend`;
  const { toast } = useToast();
  const [li, setLi] = useState(null);
  const [email, setEmail] = useState(null);
  const [busy, setBusy] = useState("");
  const [testTo, setTestTo] = useState("");

  const loadLi = () => getLinkedInStatus().then(setLi).catch(() => setLi({ configured: false, connected: false }));
  const loadEmail = () => getEmailStatus().then(setEmail).catch(() => setEmail({ enabled: false, domains: [] }));
  useEffect(() => {
    loadLi(); loadEmail();
    const q = new URLSearchParams(window.location.search).get("linkedin");
    if (q === "connected") { toast({ title: "LinkedIn connected", description: "Your company page is ready for auto-posting." }); window.history.replaceState({}, "", window.location.pathname); }
    else if (q && q !== "connected") { toast({ title: "LinkedIn connection failed", description: "Please try again and approve access." }); window.history.replaceState({}, "", window.location.pathname); }
  }, []); // eslint-disable-line

  const connect = async () => {
    setBusy("li");
    try { const r = await connectLinkedIn(); window.location.assign(r.url); }
    catch (e) { toast({ title: "Couldn't start LinkedIn connect", description: e?.response?.data?.detail || "Try again." }); setBusy(""); }
  };
  const disconnect = async () => { setBusy("li"); try { await disconnectLinkedIn(); toast({ title: "LinkedIn disconnected" }); loadLi(); } finally { setBusy(""); } };
  const sendTest = async () => {
    if (!testTo) { toast({ title: "Enter an email to send a test to" }); return; }
    setBusy("email");
    try { const r = await sendTestEmail(testTo); toast({ title: r?.sent === false ? "Not sent" : "Test email sent", description: r?.message || `Check ${testTo}` }); }
    catch (e) { toast({ title: "Couldn't send test", description: e?.response?.data?.detail || "Try again." }); }
    finally { setBusy(""); }
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast({ title: "Copied" }); };

  if (!li || !email) return <div className="flex items-center justify-center py-20 text-[#8a8a8a]"><Loader2 className="animate-spin mr-2" size={18} /> Loading…</div>;

  return (
    <div className="space-y-6" data-testid="connections-panel">
      {/* LinkedIn */}
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6" data-testid="linkedin-card">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl bg-[#0A66C2] flex items-center justify-center"><Linkedin size={22} className="text-white" /></span>
            <div>
              <h3 className="font-display text-[18px] font-500 text-[#2b2b2b]">LinkedIn auto-posting</h3>
              <p className="text-[13px] text-[#8a8a8a]">Connect your company page so approved posts publish to LinkedIn.</p>
            </div>
          </div>
          <Pill ok={li.connected} okText="Connected" noText="Not connected" />
        </div>

        {!li.configured ? (
          <div className="flex items-center gap-2 text-[13px] text-amber-700 bg-amber-50 rounded-lg p-3"><AlertTriangle size={15} /> LinkedIn app keys aren't configured on the server yet.</div>
        ) : li.connected ? (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-[13px] text-[#4a4a4a]">Posting as <strong>{li.organization || "your company page"}</strong>{li.organizationUrn ? "" : " — no page linked yet; you must be a Page admin."}</p>
            <button onClick={disconnect} disabled={busy === "li"} data-testid="linkedin-disconnect" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[#e2d8c8] text-[13px] font-500 hover:border-red-300 hover:text-red-500 transition-colors">{busy === "li" ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />} Disconnect</button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={connect} disabled={busy === "li"} data-testid="linkedin-connect" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0A66C2] text-white text-[14px] font-500 hover:opacity-90 transition-opacity disabled:opacity-60">{busy === "li" ? <Loader2 size={16} className="animate-spin" /> : <Linkedin size={16} />} Connect LinkedIn</button>
              <p className="text-[12px] text-[#a0a0a0]">One tap → approve on LinkedIn → done. You must be an admin of the company page.</p>
            </div>
            {li.redirectUri && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3.5" data-testid="linkedin-redirect-help">
                <p className="text-[12px] text-amber-800 font-500 mb-1.5 flex items-center gap-1.5"><AlertTriangle size={13} /> First-time setup — add this redirect URL to your LinkedIn app</p>
                <p className="text-[12px] text-amber-700 mb-2">In your LinkedIn app → <strong>Auth</strong> tab → <strong>Authorized redirect URLs</strong>, add this exact URL (otherwise LinkedIn shows “redirect_uri does not match”):</p>
                <div className="flex items-center gap-2 text-[11px] bg-white border border-amber-200 rounded-lg p-2 font-mono">
                  <span className="truncate flex-1" title={li.redirectUri}>{li.redirectUri}</span>
                  <button onClick={() => copy(li.redirectUri)} className="text-[#a0a0a0] hover:text-[#b8935f] shrink-0" data-testid="linkedin-redirect-copy"><Copy size={13} /></button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Email / Resend */}
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6" data-testid="email-card">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl bg-[#b8935f] flex items-center justify-center"><Mail size={22} className="text-white" /></span>
            <div>
              <h3 className="font-display text-[18px] font-500 text-[#2b2b2b]">Client email delivery</h3>
              <p className="text-[13px] text-[#8a8a8a]">Sending from <strong>{email.sender}</strong> via Resend.</p>
            </div>
          </div>
          <Pill ok={email.verified} okText="Domain verified" noText="Needs verification" />
        </div>

        <div className="rounded-xl bg-[#faf7f2] border border-[#f0ece5] p-4 mb-4" data-testid="resend-tracking-help">
          <p className="text-[13px] text-[#2b2b2b] font-500 mb-1.5 flex items-center gap-1.5"><Send size={13} className="text-[#b8935f]" /> Open &amp; click tracking (real campaign rates)</p>
          <ol className="text-[13px] text-[#6a6a6a] space-y-1 list-decimal ml-4 mb-2">
            <li>In Resend, open <a href="https://resend.com/settings" target="_blank" rel="noreferrer" className="text-[#b8935f] underline">Settings <ExternalLink size={11} className="inline" /></a> and turn on <strong>Open Tracking</strong> &amp; <strong>Click Tracking</strong>.</li>
            <li>Open <a href="https://resend.com/webhooks" target="_blank" rel="noreferrer" className="text-[#b8935f] underline">Webhooks <ExternalLink size={11} className="inline" /></a> → <em>Add Endpoint</em>, select the <code>email.opened</code>, <code>email.clicked</code> &amp; <code>email.delivered</code> events, and paste this URL:</li>
          </ol>
          <div className="flex items-center gap-2 text-[11px] bg-white border border-[#f0ece5] rounded-lg p-2 font-mono">
            <span className="truncate flex-1" title={WEBHOOK_URL}>{WEBHOOK_URL}</span>
            <button onClick={() => copy(WEBHOOK_URL)} className="text-[#a0a0a0] hover:text-[#b8935f] shrink-0" data-testid="resend-webhook-copy"><Copy size={13} /></button>
          </div>
        </div>

        {!email.verified && (
          <div className="rounded-xl bg-[#faf7f2] border border-[#f0ece5] p-4 mb-4">
            <p className="text-[13px] text-[#2b2b2b] font-500 mb-2">Verify your sending domain (so emails land in inboxes):</p>
            <ol className="text-[13px] text-[#6a6a6a] space-y-1 list-decimal ml-4 mb-3">
              <li>Open <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-[#b8935f] underline">resend.com/domains <ExternalLink size={11} className="inline" /></a> and add your domain (e.g. anadearestates.com).</li>
              <li>Add the DNS records below to your domain provider (SPF, DKIM &amp; DMARC).</li>
              <li>Click <em>Verify</em> in Resend — it can take a few minutes to propagate.</li>
              <li>Then set <code>SENDER_EMAIL</code> to an address on that domain.</li>
            </ol>
            {(email.domains || []).map((d) => (
              <div key={d.id || d.name} className="mb-3">
                <p className="text-[12px] font-500 text-[#2b2b2b] mb-1">{d.name} — <span className={d.status === "verified" ? "text-green-600" : "text-amber-600"}>{d.status}</span></p>
                {(d.records || []).length > 0 && (
                  <div className="space-y-1">
                    {d.records.map((rec, i) => (
                      <div key={i} className="flex items-center gap-2 text-[11px] bg-white border border-[#f0ece5] rounded-lg p-2 font-mono">
                        <span className="px-1.5 py-0.5 rounded bg-[#eef2f7] text-[#0A66C2] shrink-0">{rec.type}</span>
                        <span className="truncate flex-1" title={rec.value}>{rec.name} → {rec.value}</span>
                        <button onClick={() => copy(rec.value)} className="text-[#a0a0a0] hover:text-[#b8935f] shrink-0"><Copy size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {(email.domains || []).length === 0 && <p className="text-[12px] text-[#a0a0a0]">No domain added in Resend yet — add one to see its DNS records here.</p>}
          </div>
        )}

        <div className="flex items-center gap-2">
          <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@email.com" data-testid="email-test-to" className="flex-1 h-11 px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:border-[#b8935f]" />
          <button onClick={sendTest} disabled={busy === "email"} data-testid="email-test-send" className="inline-flex items-center gap-1.5 px-4 h-11 rounded-lg bg-[#1c1c1e] text-white text-[13px] font-500 hover:bg-black transition-colors disabled:opacity-60">{busy === "email" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test</button>
          <button onClick={loadEmail} className="w-11 h-11 rounded-lg bg-white border border-[#e2d8c8] flex items-center justify-center text-[#6a6a6a] hover:border-[#b8935f]" data-testid="email-refresh"><RefreshCw size={15} /></button>
        </div>
      </div>
    </div>
  );
};

export default Connections;
